import { createElement, useContext } from '@wordpress/element'
import cls from 'classnames'
import Sortable from '../placements/Sortable'
import { DragDropContext } from '../../../options/options/ct-footer-builder'
import { PanelContext } from '../../../options/components/PanelLevel'

import { customItemsSeparator, getOriginalId } from '../placements/helpers'

const shallowCompare = (prev, next) => {
	for (let key in next) {
		if (next[key] !== prev[key]) return false
	}
	return true
}

function memo(Component, areEqual = shallowCompare) {
	let prevProps = {}
	let prevResult

	return (nextProps) => {
		if (prevResult !== undefined && areEqual(prevProps, nextProps)) {
			return prevResult
		}

		prevProps = nextProps
		prevResult = createElement(Component, Object.assign({}, nextProps))

		return prevResult
	}
}

export const DraggableItem = ({
	item,
	index,
	panelType,
	onRemove,
	renderItem,
	onClick,
	className,
}) => {
	const itemData = ct_customizer_localizations.header_builder_data[
		panelType
	].find(({ id }) => id === getOriginalId(item))

	const { builderValue } = useContext(DragDropContext)

	if (renderItem) {
		return renderItem({ item, itemData, index })
	}

	/**
	 * Dynamic items have a : in their ID
	 */
	const allDynamicItems = Object.keys(builderValue.items).filter(
		(id) => id.indexOf(customItemsSeparator()) > -1
	)

	const allClonesAndOriginal = [
		getOriginalId(item),
		...allDynamicItems.filter(
			(id) => getOriginalId(id) === getOriginalId(item)
		),
	]

	return (
		<div
			data-id={item}
			className={cls('ct-builder-item', className, {
				// 'ct-is-dragging': snapshot.isDragging
			})}
			onClick={onClick}>
			{itemData && itemData.config
				? allClonesAndOriginal.length > 1
					? `${itemData.config.name} ${
							allClonesAndOriginal.indexOf(item) + 1
					  }`
					: itemData.config.name
				: item}

			<button
				className="ct-btn-remove"
				onClick={(e) => {
					e.preventDefault()
					e.stopPropagation()
					onRemove()
				}}></button>
		</div>
	)
}

const DraggableItems = ({
	items,
	draggableId,
	hasPointers = true,
	className,
	tagName = 'div',
	direction = 'horizontal',
	group = 'header_sortables',
	options = {},
	propsForItem = (item) => ({}),
	panelType = 'footer',
	displayWrapper = true,
	...props
}) => {
	const { isDragging, setIsDragging, onChange, setList } = useContext(
		DragDropContext
	)

	const { panelsHelpers } = useContext(PanelContext)

	const itemsOutput = items.map((item, index) => (
		<DraggableItem
			key={item}
			index={index}
			panelType={panelType}
			item={item}
			className={
				panelsHelpers.isOpenFor(`builder_panel_${item}`)
					? 'ct-customizing'
					: ''
			}
			onClick={(e) => {
				e.stopPropagation()
				e.preventDefault()

				panelsHelpers.open(`builder_panel_${item}`)
			}}
			onRemove={() => {
				panelsHelpers.close()

				setList({
					[draggableId]: items.filter((id) => id !== item),
				})
			}}
			{...propsForItem(item)}
		/>
	))

	if (!displayWrapper) {
		return itemsOutput
	}

	return (
		<Sortable
			options={{
				delay: 0,
				group,
				fallbackOnBody: true,
				forceFallback: true,
				fallbackTolerance: 5,
				filter: '.ct-pointer',
				direction: direction,
				touchStartThreshold: 5,
				onStart: (event) => {
					setIsDragging(event.item.dataset.id)

					document.body.classList.add('ct-builder-dragging')

					if (event.from && group && group.pull !== 'clone') {
						event.to.classList.add('ct-is-over')
					}
				},

				onEnd: () => {
					setIsDragging(false)
					document.body.classList.remove('ct-builder-dragging')
					;[
						...document.querySelectorAll(
							'.ct-panel-builder .ct-is-over'
						),
					].map((el) => el.classList.remove('ct-is-over'))
				},

				onMove: (event, originalEvent) => {
					if (event.from.closest('#ct-option-header-builder-items')) {
						Promise.resolve().then(() =>
							[
								...event.from.querySelectorAll(
									`[data-id="${event.dragged.dataset.id}"]`
								),
							].map((el) => {
								el.classList.remove('ct-builder-item')
								el.classList.add('ct-item-in-builder')
							})
						)
					}

					if (event.to) {
						if (
							event.to.children &&
							Array.from(event.to.children).filter(
								(e) =>
									e.dataset.id !== '__pointer__' &&
									e.dataset.id !== '__filler__'
							).length > 0
						) {
							return false
						}
						;[
							...document.querySelectorAll(
								'.ct-panel-builder .ct-is-over'
							),
						].map((el) => el.classList.remove('ct-is-over'))

						event.to.classList.add('ct-is-over')
					}
				},
				...options,
			}}
			onChange={(order, sortable, evt) => {
				if (draggableId === 'available-items') {
					return
				}

				onChange({
					id: draggableId,
					value: order.filter(
						(i) => i !== '__pointer__' && i !== '__filler__'
					),
				})
			}}
			tag={tagName}
			className={cls('ct-builder-items', className)}
			{...props}
			data-id={draggableId}>
			{draggableId !== 'available-items' && isDragging && (
				<div data-id="__filler__" className="ct-filler"></div>
			)}

			{hasPointers && isDragging && (
				<div data-id="__pointer__" className="ct-pointer"></div>
			)}

			{itemsOutput}
		</Sortable>
	)
}

export default DraggableItems
